import {
    DataTableBodyV2,
    DataTableCellV2,
    DataTableHeadCellV2,
    DataTableHeadV2,
    DataTableRowV2,
    DataTableV2
} from '@americanexpress/dls-react';
import React, { Fragment, useEffect, useState } from 'react';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { TransactionModel } from 'src/types/transaction.model';
import { TransactionRowModel } from 'src/components/atomic/organisms/transaction/TransactionsTable';
import { useLocation } from 'react-router-dom';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import { useSearchParams } from 'src/hooks/useSearchParams';
import { TransactionField } from 'src/bw/project-jaeger/data/TransactionField.enum';
import { Direction } from 'src/types/sort-direction.enum';
import TransactionTableCardFeatureWrapper from 'src/components/atomic/organisms/transactionTableCard/TransactionTableCard';
import TransactionRow from 'src/components/atomic/organisms/transactionTableRow/TransactionRow';
import TransactionReviewTableCardFeatureWrapper from 'src/components/atomic/organisms/transactionReviewTableCard/TransactionReviewTableCard';
import { Align } from 'src/types/align.enum';
import styles from 'src/components/atomic/organisms/transaction/transactionTableContent.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TransactionFrequencyTooltip } from 'src/components/atomic/organisms/transaction/frequencyTooltip/TransactionFrequencyTooltip';
import { WalkmeName, WalkmePopupWrapper } from 'src/components/atomic/organisms/walkme';
import { CategoryModel } from 'src/types/category.model';
import RulePrompt, { RulePromptState } from 'src/components/atomic/molecules/rulePrompt/RulePrompt';
import { useUserAttributes } from 'src/hooks/queries/users';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXP from 'src/types/one-xp';
import { shouldShowRulePromptForTransactionTable } from 'src/utils/transactions.util';

interface Props {
    transactionRows: TransactionRowModel[];
    categories: CategoryModel[];
    isReview: boolean;
    handleRowCheckChange: (event: any, row: { id: string; checked: boolean }) => void;
}

export const TransactionTableContent = ({
    handleRowCheckChange,
    categories,
    isReview,
    transactionRows
}: Props): JSX.Element => {
    const { isMinWidthSmall, isMinWidthLarge, isMinWidthExtraLarge } = useBreakpoints();
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: userAttributes } = useUserAttributes();
    const { data: transactionsContextualRulesFeatureFlag } = useFeatureFlag(
        oneXP.FeatureFlags.TransactionsContextualRules
    );
    const { pathname } = useLocation();
    const [transactionRulePrompt, setTransactionRulePrompt] = useState<TransactionModel>();
    const [rulePromptState, setRulePromptState] = useState<RulePromptState>(RulePromptState.STANDARD);
    const isCardView: boolean = !isMinWidthLarge;
    const withTypeColumn: boolean = isMinWidthExtraLarge;
    const field: TransactionField = (searchParams.get('field') as TransactionField) || TransactionField.DATE;
    const direction = (searchParams.get('direction') as Direction) || Direction.DESCENDING;
    const headerRowId: string = 'tablev2-checkbox-header-row';
    const checkedRows = transactionRows?.filter((row) => row.checked);

    /** Set indeterminate state for the header checkbox to fix ADA issue highlighted in ticket - BANK-6233.
     * This actually a DLS issue, but we are handling it here until DLS releases their fix for it.
     * TODO: remove this code once DLS releases the fix and we have updated DLS version in our project.
     */
    useEffect(() => {
        // Set indeterminate state for the header checkbox if one or more rows are checked, but not all rows are checked.
        if (isMinWidthLarge && checkedRows?.length > 0 && checkedRows?.length < transactionRows?.length) {
            const headerCheckbox = document.getElementById(headerRowId + '-check') as HTMLInputElement;
            if (headerCheckbox) headerCheckbox.indeterminate = true;
        }
    }, [isMinWidthLarge, checkedRows, transactionRows]);

    /** We need this useEffect so that we can add the rule prompt component into the transaction table, as a table
     *  row. The rulePromptTableRow is a dummy row that is inserted into the table and sits below the rulePromptComponent.
     *  This was done because it was difficult to position the rulePromptComponent correctly in the table without
     *  breaking the table layout. rulePromptComponent is absolutely positioned on top of the rulePromptTableRow.
     */
    useEffect(() => {
        const rulePromptTableRow = document.getElementById(`table-row-rule-prompt-${transactionRulePrompt?.id}`);
        const rulePromptComponent = document.getElementById('rule-prompt');
        // If the rule prompt is not to be displayed or the rule prompt table row is already at the correct height, return
        if (!transactionRulePrompt || rulePromptTableRow?.clientHeight === rulePromptComponent?.clientHeight) return;
        if (rulePromptTableRow && rulePromptComponent) {
            // Adjust the height of the transaction table row to the height of the absolute positioned rule prompt component
            rulePromptTableRow.style.height = `${rulePromptComponent.clientHeight}px`;
        }
    }, [transactionRulePrompt, rulePromptState, transactionRows, isMinWidthLarge]);

    /** Reset the rule prompt state to standard when we have to display rule prompt for a new transaction */
    useEffect(() => {
        setRulePromptState(RulePromptState.STANDARD);
    }, [transactionRulePrompt]);

    const approveHeader: JSX.Element = (
        <DataTableHeadCellV2 align={Align.LEFT} className={styles.approveHeader}>
            {LiteralsService.getLiteral('TransactionsTable.heading.approve')}
        </DataTableHeadCellV2>
    );
    const typeHeader: JSX.Element = withTypeColumn ? (
        isReview ? (
            <DataTableHeadCellV2
                data-cy={Selectors.Transactions.TYPE_COLUMN}
                sortable
                id={TransactionField.TYPE}
                className={styles.typeHeader}
            >
                {LiteralsService.getLiteral('TransactionsTable.heading.type')}
            </DataTableHeadCellV2>
        ) : (
            <WalkmePopupWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={3}>
                <DataTableHeadCellV2
                    data-cy={Selectors.Transactions.TYPE_COLUMN}
                    sortable
                    id={TransactionField.TYPE}
                    className={styles.typeHeader}
                >
                    {LiteralsService.getLiteral('TransactionsTable.heading.type')}
                </DataTableHeadCellV2>
            </WalkmePopupWrapper>
        )
    ) : (
        <></>
    );

    /** Sets the new sort direction when the date sort on the table is clicked */
    const handleSortClick = (_event: Event, id: TransactionField): void => {
        trackClick(pathname, `${adobeTags.Transactions.SortBy}${id}`);
        let newDirection;
        if (field === id) newDirection = direction === Direction.ASCENDING ? Direction.DESCENDING : Direction.ASCENDING;
        else newDirection = id === TransactionField.DATE ? Direction.DESCENDING : Direction.ASCENDING;

        searchParams.set('page', '1');
        searchParams.set('direction', newDirection);
        searchParams.set('field', id);
        setSearchParams(searchParams);
    };

    const rows: JSX.Element[] = transactionRows?.map((transactionRow: TransactionRowModel, index) => {
        const transaction: TransactionModel = transactionRow.transaction;
        const showTransactionRulePrompt: boolean =
            transactionsContextualRulesFeatureFlag &&
            shouldShowRulePromptForTransactionTable(
                transactionRulePrompt?.id,
                transaction.id,
                userAttributes,
                rulePromptState
            );
        if (!isCardView)
            return (
                <>
                    <TransactionRow
                        transaction={transaction}
                        categories={categories}
                        key={index}
                        isReview={isReview}
                        rowIndex={index}
                        checked={transactionRow.checked}
                        setDisplayingTransactionRulePrompt={
                            transactionsContextualRulesFeatureFlag ? setTransactionRulePrompt : undefined
                        }
                    />
                    {showTransactionRulePrompt && (
                        <DataTableRowV2
                            id={`table-row-rule-prompt-${transactionRulePrompt?.id}`}
                            className={styles.rulePromptRow}
                            checkboxLabel="Rule Prompt"
                            role="presentation"
                        >
                            <DataTableCellV2>
                                <RulePrompt
                                    className={styles.rulePrompt}
                                    transaction={transactionRulePrompt}
                                    onClose={() => {
                                        setTransactionRulePrompt(undefined);
                                        setRulePromptState(RulePromptState.STANDARD);
                                    }}
                                    state={rulePromptState}
                                    setState={setRulePromptState}
                                />
                            </DataTableCellV2>
                        </DataTableRowV2>
                    )}
                </>
            );

        return (
            <Fragment key={index}>
                {isCardView && isReview && (
                    <TransactionReviewTableCardFeatureWrapper
                        transaction={transaction}
                        categories={categories}
                        rowIndex={index}
                        rulePromptState={rulePromptState}
                        setRulePromptState={setRulePromptState}
                        displayingTransactionRulePrompt={transactionRulePrompt}
                        setDisplayingTransactionRulePrompt={setTransactionRulePrompt}
                    />
                )}
                {isCardView && !isReview && (
                    <TransactionTableCardFeatureWrapper
                        transaction={transaction}
                        categories={categories}
                        rowIndex={index}
                        rulePromptState={rulePromptState}
                        setRulePromptState={setRulePromptState}
                        displayingTransactionRulePrompt={transactionRulePrompt}
                        setDisplayingTransactionRulePrompt={setTransactionRulePrompt}
                    />
                )}
            </Fragment>
        );
    });

    const dataTable: JSX.Element = (
        <DataTableV2
            checkable
            id="tablev2-transaction-table"
            data-cy={Selectors.Transactions.TABLE_CONTAINER}
            sortable
            sortBy={field}
            sortDirection={direction}
            onSortClick={handleSortClick}
            onRowCheckChange={handleRowCheckChange}
            onHeaderRowCheckChange={() => {}}
        >
            <DataTableHeadV2>
                <DataTableRowV2
                    checkboxLabel={LiteralsService.getLiteral('TransactionsTable.heading.selectAll')}
                    id={headerRowId}
                    data-cy={Selectors.Transactions.TABLE_HEADER_ROW}
                    className={styles.headingRow}
                >
                    <DataTableHeadCellV2
                        sortable
                        id={TransactionField.NAME}
                        className={`${styles.nameHeader} ${isReview ? styles.nameHeaderReview : ''}`}
                    >
                        {LiteralsService.getLiteral('TransactionsTable.heading.name')}
                    </DataTableHeadCellV2>
                    <DataTableHeadCellV2
                        sortable
                        id={TransactionField.AMOUNT}
                        align={Align.RIGHT}
                        className={styles.amountHeader}
                    >
                        {LiteralsService.getLiteral('TransactionsTable.heading.amount')}
                    </DataTableHeadCellV2>
                    <DataTableHeadCellV2
                        sortable
                        id={TransactionField.DATE}
                        className={styles.dateHeader}
                        align={Align.CENTER}
                    >
                        {LiteralsService.getLiteral('TransactionsTable.heading.date')}
                    </DataTableHeadCellV2>
                    {typeHeader}
                    <DataTableHeadCellV2
                        sortable
                        id={TransactionField.CATEGORY}
                        className={`${styles.categorySourceHeader} ${isReview ? styles.dropdownHeaderReview : ''}`}
                    >
                        {LiteralsService.getLiteral('TransactionsTable.heading.category')}
                    </DataTableHeadCellV2>
                    <DataTableHeadCellV2
                        id={TransactionField.OCCURRENCE}
                        className={`${styles.frequencyHeader} ${isReview ? styles.dropdownHeaderReview : ''}`}
                    >
                        {LiteralsService.getLiteral('TransactionsTable.heading.frequency.title')}
                        <TransactionFrequencyTooltip
                            className={styles.transactionsTableFrequencyTooltip}
                            onClick={() => trackClick(pathname, adobeTags.Tooltip.Frequency)}
                        />
                    </DataTableHeadCellV2>
                    <DataTableHeadCellV2 className={`${isReview ? styles.editHeaderReview : styles.editHeader}`}>
                        {LiteralsService.getLiteral('TransactionsTable.heading.edit')}
                    </DataTableHeadCellV2>
                    {isReview ? approveHeader : <></>}
                </DataTableRowV2>
            </DataTableHeadV2>
            <DataTableBodyV2>{rows}</DataTableBodyV2>
        </DataTableV2>
    );

    if (isMinWidthLarge) {
        return <>{dataTable}</>;
    }

    if (isMinWidthSmall) {
        return <div>{rows}</div>;
    }

    return isReview ? (
        <div>{rows}</div>
    ) : (
        <WalkmePopupWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={4}>
            <WalkmePopupWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={3}>
                <div>{rows}</div>
            </WalkmePopupWrapper>
        </WalkmePopupWrapper>
    );
};
